import React, { useEffect, useState } from 'react';
import AdminShell from './AdminShell';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { ShieldAlert, ShieldCheck, Trash2, Mail, MailX, MailWarning } from 'lucide-react';

const EMAIL_BADGE = {
  sent: ['bg-emerald-50 text-emerald-700', Mail, 'Emailed'],
  failed: ['bg-red-50 text-red-700', MailX, 'Email failed'],
  skipped: ['bg-[var(--bg-alt)] text-ink-3', MailWarning, 'Not emailed'],
  pending: ['bg-[var(--bg-alt)] text-ink-3', MailWarning, 'Not emailed'],
};

const AdminContacts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('inbox');
  const [openId, setOpenId] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    try {
      const res = await api.get('/admin/contacts');
      setItems(res.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setSpam = async (c, spam) => {
    try {
      const res = await api.post(`/admin/contacts/${c.id}/spam?spam=${spam}`);
      toast({
        title: spam ? 'Moved to spam' : 'Marked as legitimate',
        description: !spam ? (res.data.email_status === 'sent' ? 'The team has been emailed.' : `Email status: ${res.data.email_status}`) : undefined,
      });
      load();
    } catch (e) {
      toast({ title: 'Update failed', variant: 'destructive' });
    }
  };

  const remove = async (c) => {
    if (!confirm('Delete this submission permanently?')) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      load();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const inbox = items.filter((c) => !c.spam);
  const spam = items.filter((c) => c.spam);
  const list = tab === 'inbox' ? inbox : spam;

  return (
    <AdminShell title="Contact submissions">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="inline-flex rounded-full border border-[var(--line-2)] bg-white p-1 text-sm">
          {[['inbox', `Inbox (${inbox.length})`], ['spam', `Spam (${spam.length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`contacts-tab-${k}`} className={`px-4 py-1.5 rounded-full transition-colors ${tab === k ? 'bg-[var(--ink)] text-white' : 'text-ink-2 hover:text-ink'}`}>{label}</button>
          ))}
        </div>
        <div className="text-xs text-ink-3">Legitimate messages are emailed to the sales team instantly. Suspected spam is quarantined here and never emailed.</div>
      </div>
      <div className="bg-white rounded-2xl border border-[var(--line-2)] overflow-hidden" data-testid="contacts-table">
        {loading ? (
          <div className="p-8 text-center text-ink-3 text-sm">Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-ink-3 text-sm">{tab === 'inbox' ? 'No submissions yet.' : 'No spam caught. Nice.'}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-alt)] text-ink-3 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Received</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Interest</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Channel</th>
                <th className="text-left px-4 py-3">{tab === 'inbox' ? 'Delivery' : 'Flags'}</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const [cls, Icon, label] = EMAIL_BADGE[c.email_status] || EMAIL_BADGE.pending;
                const open = openId === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr className="border-t border-[var(--line-2)] align-top cursor-pointer hover:bg-[var(--bg-alt)]/50" onClick={() => setOpenId(open ? null : c.id)} data-testid={`contact-row-${c.id}`}>
                      <td className="px-4 py-3 text-ink-3 whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-ink font-medium">{c.name}</td>
                      <td className="px-4 py-3"><a href={`mailto:${c.email}`} className="text-ink hover:underline" onClick={(e) => e.stopPropagation()}>{c.email}</a>{c.phone && <div className="text-[11px] text-ink-3">{c.phone}</div>}</td>
                      <td className="px-4 py-3 text-ink-2">{c.company}</td>
                      <td className="px-4 py-3 text-ink-2">{c.product}</td>
                      <td className="px-4 py-3 text-ink-3">{c.source}</td>
                      <td className="px-4 py-3 text-ink-2 text-xs" data-testid={`contact-channel-${c.id}`}>
                        {c.attribution?.utm_source ? `${c.attribution.utm_source}${c.attribution.utm_medium ? ` / ${c.attribution.utm_medium}` : ''}` : '—'}
                        {c.attribution?.utm_campaign && <div className="text-[10px] text-ink-3">{c.attribution.utm_campaign}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {tab === 'inbox' ? (
                          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${cls}`} title={c.email_ref}><Icon className="w-3 h-3" /> {label}</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">{(c.spam_reasons || []).map((r) => <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{r}</span>)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-2">
                          {c.spam ? (
                            <button onClick={() => setSpam(c, false)} className="text-ink-2 hover:text-emerald-700" title="Not spam — deliver to team" data-testid={`contact-notspam-${c.id}`}><ShieldCheck className="w-4 h-4" /></button>
                          ) : (
                            <button onClick={() => setSpam(c, true)} className="text-ink-2 hover:text-amber-600" title="Mark as spam" data-testid={`contact-spam-${c.id}`}><ShieldAlert className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => remove(c)} className="text-ink-2 hover:text-red-600" title="Delete" data-testid={`contact-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-[var(--bg-alt)]/60">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="text-[11px] uppercase tracking-wider text-ink-3 mb-1">Message</div>
                          <div className="text-sm text-ink whitespace-pre-wrap max-w-3xl">{c.message || '—'}</div>
                          <div className="text-[11px] text-ink-3 mt-3">Role: {c.role || '—'} · IP: {c.ip || '—'}{c.email_ref && c.email_status !== 'sent' ? ` · ${c.email_ref}` : ''}</div>
                          {c.attribution && Object.keys(c.attribution).length > 0 && (
                            <div className="text-[11px] text-ink-3 mt-1">
                              Attribution: landed on {c.attribution.landing_page || '—'} · referrer {c.attribution.referrer || 'none'} · submitted from {c.attribution.submitted_from || '—'}
                              {c.attribution.utm_term ? ` · term "${c.attribution.utm_term}"` : ''}{c.attribution.utm_content ? ` · content "${c.attribution.utm_content}"` : ''}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminContacts;
